import { DragDropModule } from '@angular/cdk/drag-drop';
import { TextFieldModule } from '@angular/cdk/text-field';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule } from '@auth0/angular-jwt';
import { ResizableModule } from 'angular-resizable-element';
import { BarChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxEchartsModule } from 'ngx-echarts';
import { NgxEditorModule } from 'ngx-editor';
import { TokenInterceptor } from 'src/app/interceptors/token.interceptor';
import { AuthService, ID_TOKEN_NAME } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { SafePipe } from 'src/utils/SafePipe';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccountSettingsComponent } from './components/account-settings/account-settings.component';
import { ArticlesReviewComponent } from './components/articles-review/articles-review.component';
import { AutocompleteWrapperComponent } from './components/autocomplete-wrapper/autocomplete-wrapper.component';
import { ChipsWrapperComponent } from './components/chips-wrapper/chips-wrapper.component';
import { CommonTableComponent } from './components/common-table/common-table.component';
import { EngagementImportComponent } from './components/common-table/engagement-import/engagement-import.component';
import { MergeComponent } from './components/common-table/merge/merge.component';
import { OptInOutComponent } from './components/common-table/opt-in-out/opt-in-out.component';
import { SimpleHcpEditorComponent } from './components/common-table/opt-in-out/simple-hcp-editor/simple-hcp-editor.component';
import { PaginationComponent } from './components/common-table/pagination/pagination.component';
import { PrioritySettingsComponent } from './components/common-table/priority-settings/priority-settings.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { CounterComponent } from './components/counter/counter.component';
import { DataMaintenanceFooterButtonComponent } from './components/data-mainterance-fotter-button/data-maintenance-footer-button.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { ImportExportPanelComponent } from './components/import-export-panel/import-export-panel.component';
import { ImportComponent } from './components/import/import.component';
import { LoadingComponent } from './components/loading/loading.component';
import { MarkdownComponent } from './components/markdown/markdown.component';
import { NoteComponent } from './components/note/note.component';
import { ProjectRegionSelectorComponent } from './components/projects-selector/project-region-selector/project-region-selector.component';
import { ProjectsSelectorComponent } from './components/projects-selector/projects-selector.component';
import { RegionSelectComponent } from './components/region-select/region-select.component';
import { ScoreViewComponent } from './components/score-view/score-view.component';
import { SettingsInitializatorComponent } from './components/settings-initializator/settings-initializator.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { ValueEditorComponent } from './components/value-editor/value-editor.component';
import { AliasesComponent } from './pages/center/center/aliases/aliases.component';
import { CenterProfileComponent } from './pages/center/center/center-profile/center-profile.component';
import { CustomMetricsComponent } from './pages/center/center/center-profile/custom-metrics/custom-metrics.component';
import { CenterComponent } from './pages/center/center/center.component';
import { HcpsTableComponent } from './pages/center/center/hcps-table/hcps-table.component';
import { SalesDataWrapperComponent } from './pages/center/center/sales-data-wrapper/sales-data-wrapper.component';
import { SalesTableComponent } from './pages/center/center/sales-table/sales-table.component';
import { TierComponentComponent } from './pages/center/center/tier-component/tier-component.component';
import { CentersReviewComponent } from './pages/center/centers-review/centers-review.component';
import { DashboardComponent } from './pages/dashboard/dashboard/dashboard.component';
import { ProjectsReviewComponent } from './pages/dashboard/projects-review/projects-review.component';
import { CenterInformationComponent } from './pages/hcp/hcp/hcp-profile-tab/center-information/center-information.component';
import { ConsentDataCaptureComponent } from './pages/hcp/hcp/hcp-profile-tab/consent-data-capture/consent-data-capture.component';
import { EngagementPlanItemComponent } from './pages/hcp/hcp/hcp-profile-tab/engagement-plan/engagement-plan-item/engagement-plan-item.component';
import { EngagementPlanComponent } from './pages/hcp/hcp/hcp-profile-tab/engagement-plan/engagement-plan.component';
import { EngagementTrackingItemComponent } from './pages/hcp/hcp/hcp-profile-tab/engagement-tracking/engagement-tracking-item/engagement-tracking-item.component';
import { EngagementTrackingComponent } from './pages/hcp/hcp/hcp-profile-tab/engagement-tracking/engagement-tracking.component';
import { HcpProfileTabComponent } from './pages/hcp/hcp/hcp-profile-tab/hcp-profile-tab.component';
import { InfluenceComponent } from './pages/hcp/hcp/hcp-profile-tab/influence/influence.component';
import { KeyInformationComponent } from './pages/hcp/hcp/hcp-profile-tab/key-information/key-information.component';
import { ProfileDetailsDescriptionComponent } from './pages/hcp/hcp/hcp-profile-tab/profile-details-description/profile-details-description.component';
import { ProfileDetailsComponent } from './pages/hcp/hcp/hcp-profile-tab/profile-details/profile-details.component';
import { TwitterReviewComponent } from './pages/hcp/hcp/hcp-profile-tab/profile-details/twitter-review/twitter-review.component';
import { SimpleProximityComponent } from './pages/hcp/hcp/hcp-profile-tab/simple-proximity/simple-proximity.component';
import { StaticBooleanComponent } from './pages/hcp/hcp/hcp-profile-tab/static-boolean/static-boolean.component';
import { ToggleButtonWrapperComponent } from './pages/hcp/hcp/hcp-profile-tab/toggle-button-wrapper/toggle-button-wrapper.component';
import { TweetsComponent } from './pages/hcp/hcp/hcp-profile-tab/tweets/tweets.component';
import { ValueComponent } from './pages/hcp/hcp/hcp-profile-tab/value/value.component';
import { HcpComponent } from './pages/hcp/hcp/hcp.component';
import { OtherComponent } from './pages/hcp/hcp/other/other.component';
import { ProximityScoreComponent } from './pages/hcp/hcp/proximity-score/proximity-score.component';
import { PublicationsComponent } from './pages/hcp/hcp/publications/publications.component';
import { StudiesComponent } from './pages/hcp/hcp/studies/studies.component';
import { ExtendedDownloadComponent } from './pages/hcp/hcps-view/extended-download/extended-download.component';
import { HcpsViewComponent } from './pages/hcp/hcps-view/hcps-view.component';
import { LoginPageComponent } from './pages/login/login-page/login-page.component';
import { NewCategoryDialog, NewProjectComponent } from './pages/new-project/new-project/new-project.component';
import { PageNotFoundComponent } from './pages/not-found/page-not-found/page-not-found.component';
import { OptInComponent } from './pages/opt-in/opt-in/opt-in.component';
import { ProjectReviewComponent } from './pages/project/project-review/project-review.component';
import { AccountRegionItemComponent } from './pages/project/project-review/project-settings/account-regions/account-region-item/account-region-item.component';
import { AccountRegionsComponent } from './pages/project/project-review/project-settings/account-regions/account-regions.component';
import { BaseSettingsComponent } from './pages/project/project-review/project-settings/base-settings/base-settings.component';
import { BatchUpdateComponent } from './pages/project/project-review/project-settings/batch-update/batch-update.component';
import { TransformationItemComponent } from './pages/project/project-review/project-settings/batch-update/transformation-item/transformation-item.component';
import { BricksCompetitorsComponent } from './pages/project/project-review/project-settings/bricks-competitors/bricks-competitors.component';
import { BricksComponent } from './pages/project/project-review/project-settings/bricks/bricks.component';
import { CenterFieldsComponent } from './pages/project/project-review/project-settings/center-fields/center-fields.component';
import { CompetitorsComponent } from './pages/project/project-review/project-settings/competitors/competitors.component';
import { FilterItemComponent } from './pages/project/project-review/project-settings/filters/filter-item/filter-item.component';
import { FiltersComponent } from './pages/project/project-review/project-settings/filters/filters.component';
import { ProductUseScoreItemComponent } from './pages/project/project-review/project-settings/product-use-scoring/product-use-score-item/product-use-score-item.component';
import { ProductUseScoringComponent } from './pages/project/project-review/project-settings/product-use-scoring/product-use-scoring.component';
import { ProjectSettingsComponent } from './pages/project/project-review/project-settings/project-settings.component';
import { PromptsComponent } from './pages/project/project-review/project-settings/prompts/prompts.component';
import { ProximityComponent } from './pages/project/project-review/project-settings/proximity/proximity.component';
import { RegionsFilesComponent } from './pages/project/project-review/project-settings/regions-files/regions-files.component';
import { RegionItemComponent } from './pages/project/project-review/project-settings/regions/region-item/region-item.component';
import { RegionsComponent } from './pages/project/project-review/project-settings/regions/regions.component';
import { ScoringItemComponent } from './pages/project/project-review/project-settings/scoring-settings/scoring-item/scoring-item.component';
import { ScoringSettingsComponent } from './pages/project/project-review/project-settings/scoring-settings/scoring-settings.component';
import { TagsSettingsComponent } from './pages/project/project-review/project-settings/tags-settings/tags-settings.component';
import { TierDefinitionsComponent } from './pages/project/project-review/project-settings/tier-definitions/tier-definitions.component';
import { TierItemComponent } from './pages/project/project-review/project-settings/tier-settings/tier-item/tier-item.component';
import { TierSettingsComponent } from './pages/project/project-review/project-settings/tier-settings/tier-settings.component';
import { TreatmentGoalsComponent } from './pages/project/project-review/project-settings/treatment-goals/treatment-goals.component';
import { TreatmentItemComponent } from './pages/project/project-review/project-settings/treatment-item/treatment-item.component';
import { TreatmentOptionsComponent } from './pages/project/project-review/project-settings/treatment-options/treatment-options.component';
import { TreatmentUsesComponent } from './pages/project/project-review/project-settings/treatment-uses/treatment-uses.component';
import { StudiesReviewComponent } from './pages/project/project-review/studies-review/studies-review.component';
import { AccountLeaderboardComponent } from './pages/report/report-page/components/kam-reports/components/account-leaderboard/account-leaderboard.component';
import { AccountsComponent } from './pages/report/report-page/components/kam-reports/components/accounts/accounts.component';
import { ChartPatientsComponent } from './pages/report/report-page/components/kam-reports/components/chart-patients/chart-patients.component';
import { ChartTreatmentGoalsComponent } from './pages/report/report-page/components/kam-reports/components/chart-treatment-goals/chart-treatment-goals.component';
import { ChartTreatmentUsedComponent } from './pages/report/report-page/components/kam-reports/components/chart-treatment-used/chart-treatment-used.component';
import { CurrentBusinessComponent } from './pages/report/report-page/components/kam-reports/components/current-business/current-business.component';
import { KamLeaderboardComponent } from './pages/report/report-page/components/kam-reports/components/kam-leaderboard/kam-leaderboard.component';
import { MarketAndPatientShareComponent } from './pages/report/report-page/components/kam-reports/components/market-and-patient-share/market-and-patient-share.component';
import { SalesInPackComponent } from './pages/report/report-page/components/kam-reports/components/sales-in-pack/sales-in-pack.component';
import { SalesMomentumComponent } from './pages/report/report-page/components/kam-reports/components/sales-momentum/sales-momentum.component';
import { TreatmentUsedComponent } from './pages/report/report-page/components/kam-reports/components/treatment-used/treatment-used.component';
import { KamReportsComponent } from './pages/report/report-page/components/kam-reports/kam-reports.component';
import { ReportCustomMetricComponent } from './pages/report/report-page/components/reports-review/report-custom-metric/report-custom-metric.component';
import { ReportFiltersItemComponent } from './pages/report/report-page/components/reports-review/report-filters/report-filters-item/report-filters-item.component';
import { ReportFiltersComponent } from './pages/report/report-page/components/reports-review/report-filters/report-filters.component';
import { ReportComponent } from './pages/report/report-page/components/reports-review/report/report.component';
import { ReportsReviewComponent } from './pages/report/report-page/components/reports-review/reports-review.component';
import { ReportPageComponent } from './pages/report/report-page/report-page.component';
import { PdfViewerModalComponent } from './pages/support/support-page/pdf-viewer-modal/pdf-viewer-modal.component';
import { SupportPageComponent } from './pages/support/support-page/support-page.component';
import { CategoriesSettingsComponent } from './pages/system-settings/system-settings/categories-settings/categories-settings.component';
import { CountriesWithPaperNotificationsDialogComponent } from './pages/system-settings/system-settings/countries-with-paper-notifications-dialog/countries-with-paper-notifications-dialog.component';
import { CustomRegionsDialogComponent } from './pages/system-settings/system-settings/custom-regions-dialog/custom-regions-dialog.component';
import { EditorComponent } from './pages/system-settings/system-settings/editor/editor.component';
import { SupportDocsComponent } from './pages/system-settings/system-settings/support-docs/support-docs.component';
import { SystemSettingsComponent } from './pages/system-settings/system-settings/system-settings.component';
import { NewUserDialogComponent } from './pages/system-settings/system-settings/users-management/new-user-dialog/new-user-dialog.component';
import { UserComponent } from './pages/system-settings/system-settings/users-management/user/user.component';
import { UsersBatchUpdateComponent } from './pages/system-settings/system-settings/users-management/users-batch-update/users-batch-update.component';
import { UsersManagementComponent } from './pages/system-settings/system-settings/users-management/users-management.component';
import { ArticlesService } from './services/articles.service';
import { CacheService } from './services/cache.service';
import { CategoriesService } from './services/categories.service';
import { CentersService } from './services/centers.service';
import { ClinicalTrialsService } from './services/clinical-trials.service';
import { CommonService } from './services/common.service';
import { DataProcessorService } from './services/data-processor.service';
import { HCPService } from './services/hcp.service';
import { ImportService } from './services/import.service';
import { LoadingService } from './services/loading.service';
import { MomentumService } from './services/momentum.service';
import { PagingService } from './services/paging.service';
import { PaperNotificationService } from './services/paper-notification.service';
import { PDFService } from './services/pdf.service';
import { RegionsService } from './services/regions.service';
import { RiseAppApiService } from './services/rise-app-api.service';
import { ScoringService } from './services/scoring.service';
import { SearchService } from './services/search.service';
import { SettingsService } from './services/settings.service';
import { SSEService } from './services/sse.service';
import { SupportDocumentsService } from './services/support-documents.service';
import { TwitterService } from './services/twitter.service';

echarts.use([
  BarChart,
  PieChart,
  GridComponent,
  CanvasRenderer,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
]);

export function tokenGetter() {
  return localStorage.getItem(ID_TOKEN_NAME);
}
@NgModule({
  declarations: [
    AccountLeaderboardComponent,
    AccountRegionItemComponent,
    AccountRegionsComponent,
    AccountsComponent,
    AccountSettingsComponent,
    AliasesComponent,
    AppComponent,
    ArticlesReviewComponent,
    AutocompleteWrapperComponent,
    BaseSettingsComponent,
    BatchUpdateComponent,
    BricksComponent,
    BricksCompetitorsComponent,
    CategoriesSettingsComponent,
    CenterComponent,
    CenterFieldsComponent,
    CenterInformationComponent,
    CenterProfileComponent,
    CentersReviewComponent,
    ChartPatientsComponent,
    ChartTreatmentGoalsComponent,
    ChartTreatmentUsedComponent,
    ChipsWrapperComponent,
    CommonTableComponent,
    CompetitorsComponent,
    ConfirmationDialogComponent,
    ConsentDataCaptureComponent,
    CounterComponent,
    CountriesWithPaperNotificationsDialogComponent,
    CurrentBusinessComponent,
    CustomMetricsComponent,
    CustomRegionsDialogComponent,
    DashboardComponent,
    DataMaintenanceFooterButtonComponent,
    EditorComponent,
    EngagementImportComponent,
    EngagementPlanComponent,
    EngagementPlanItemComponent,
    EngagementTrackingComponent,
    EngagementTrackingItemComponent,
    ExtendedDownloadComponent,
    FilterItemComponent,
    FiltersComponent,
    FooterComponent,
    HcpComponent,
    HcpProfileTabComponent,
    HcpsTableComponent,
    HcpsViewComponent,
    HeaderComponent,
    ImportComponent,
    ImportExportPanelComponent,
    InfluenceComponent,
    InfluenceComponent,
    KamLeaderboardComponent,
    KamReportsComponent,
    KeyInformationComponent,
    LoadingComponent,
    LoginPageComponent,
    MarkdownComponent,
    MarketAndPatientShareComponent,
    MergeComponent,
    NewCategoryDialog,
    NewProjectComponent,
    NewUserDialogComponent,
    NoteComponent,
    OptInComponent,
    OptInOutComponent,
    OtherComponent,
    PageNotFoundComponent,
    PaginationComponent,
    PdfViewerModalComponent,
    PrioritySettingsComponent,
    ProductUseScoreItemComponent,
    ProductUseScoringComponent,
    ProfileDetailsComponent,
    ProfileDetailsDescriptionComponent,
    ProjectRegionSelectorComponent,
    ProjectReviewComponent,
    ProjectSettingsComponent,
    ProjectsReviewComponent,
    ProjectsSelectorComponent,
    PromptsComponent,
    ProximityComponent,
    ProximityScoreComponent,
    PublicationsComponent,
    RegionItemComponent,
    RegionsComponent,
    RegionsFilesComponent,
    RegionSelectComponent,
    ReportComponent,
    ReportCustomMetricComponent,
    ReportFiltersComponent,
    ReportFiltersItemComponent,
    ReportPageComponent,
    ReportsReviewComponent,
    SafePipe,
    SalesDataWrapperComponent,
    SalesInPackComponent,
    SalesMomentumComponent,
    SalesTableComponent,
    ScoreViewComponent,
    ScoringItemComponent,
    ScoringSettingsComponent,
    SettingsInitializatorComponent,
    SimpleHcpEditorComponent,
    SimpleProximityComponent,
    StaticBooleanComponent,
    StudiesComponent,
    StudiesReviewComponent,
    SupportDocsComponent,
    SupportPageComponent,
    SystemSettingsComponent,
    TagsSettingsComponent,
    TierComponentComponent,
    TierDefinitionsComponent,
    TierItemComponent,
    TierSettingsComponent,
    ToggleButtonWrapperComponent,
    ToolbarComponent,
    TransformationItemComponent,
    TreatmentGoalsComponent,
    TreatmentItemComponent,
    TreatmentOptionsComponent,
    TreatmentUsedComponent,
    TreatmentUsesComponent,
    TweetsComponent,
    TwitterReviewComponent,
    UserComponent,
    UsersBatchUpdateComponent,
    UsersManagementComponent,
    ValueComponent,
    ValueEditorComponent,
    RegionsFilesComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    DragDropModule,
    FormsModule,
    HttpClientModule,
    JwtModule.forRoot({
      config: {
        tokenGetter,
        allowedDomains: [`http://localhost:4000`, `${environment.apiUrl}`],
      },
    }),
    MatAutocompleteModule,
    MatBadgeModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatStepperModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTreeModule,
    NgxEchartsModule.forRoot({ echarts }),
    NgxEditorModule,
    PdfViewerModule,
    ReactiveFormsModule,
    ResizableModule,
    TextFieldModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    ArticlesService,
    AuthService,
    CacheService,
    CategoriesService,
    CentersService,
    ClinicalTrialsService,
    CommonService,
    DataProcessorService,
    HCPService,
    ImportService,
    LoadingService,
    MomentumService,
    PagingService,
    PaperNotificationService,
    PDFService,
    RegionsService,
    RiseAppApiService,
    ScoringService,
    SearchService,
    SettingsService,
    SSEService,
    SupportDocumentsService,
    TwitterService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {}
}
